import React, { useState, useEffect } from 'react'
import {
  DevicePhoneMobileIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  BeakerIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { smsConfigApi } from '../../services/adminApi'

interface SmsConfig {
  id: number
  platform_code: string
  platform_name: string
  is_active: number
  app_key: string
  master_secret: string
  dev_key: string
  api_dev_secret: string
  sign_id: string
  template_id: string
  api_url: string
  rate_limit_per_ip: number
  rate_limit_per_phone: number
  code_expire_minutes: number
  code_length: number
  has_app_key: boolean
  has_master_secret: boolean
  has_dev_key: boolean
  has_api_dev_secret: boolean
}

interface Balance {
  app_balance?: number | null
  app_voice?: number | null
  app_industry?: number | null
  app_market?: number | null
  dev_balance?: number | null
}

export default function SmsConfigManagement() {
  const [configs, setConfigs] = useState<SmsConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showTestModal, setShowTestModal] = useState(false)
  const [editingConfig, setEditingConfig] = useState<SmsConfig | null>(null)
  const [showSecrets, setShowSecrets] = useState(false)
  const [balance, setBalance] = useState<Balance | null>(null)
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [stats, setStats] = useState<any>(null)

  const [formData, setFormData] = useState({
    platform_code: '',
    platform_name: '',
    app_key: '',
    master_secret: '',
    dev_key: '',
    api_dev_secret: '',
    sign_id: '',
    template_id: '',
    api_url: 'https://api.sms.jpush.cn/v1',
    rate_limit_per_ip: 10,
    rate_limit_per_phone: 5,
    code_expire_minutes: 5,
    code_length: 6
  })

  const [testPhone, setTestPhone] = useState('')
  const [testResult, setTestResult] = useState<any>(null)
  const [testing, setTesting] = useState(false)
  
  // 验证码验证测试状态
  const [verifyCode, setVerifyCode] = useState('')
  const [verifyResult, setVerifyResult] = useState<any>(null)
  const [verifying, setVerifying] = useState(false)

  // 加载配置列表
  const loadConfigs = async () => {
    setLoading(true)
    try {
      const response = await smsConfigApi.getAll()
      if (response.success) {
        setConfigs(response.data || [])
      }
    } catch (error) {
      console.error('加载短信配置失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 加载统计数据
  const loadStats = async () => {
    try {
      const response = await smsConfigApi.getStats()
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('加载统计失败:', error)
    }
  }

  // 查询余量
  const loadBalance = async () => {
    setBalanceLoading(true)
    try {
      const response = await smsConfigApi.getBalance()
      if (response.success && response.data) {
        setBalance({
          ...response.data.app_balance,
          ...response.data.dev_balance
        })
      }
    } catch (error) {
      console.error('查询余量失败:', error)
    } finally {
      setBalanceLoading(false)
    }
  }

  useEffect(() => {
    loadConfigs()
    loadStats()
    loadBalance()
  }, [])

  // 打开编辑弹窗
  const openEditModal = async (config?: SmsConfig) => {
    if (config) {
      // 获取完整配置
      const response = await smsConfigApi.getById(config.id)
      if (response.success && response.data) {
        const fullConfig = response.data
        setEditingConfig(config)
        setFormData({
          platform_code: fullConfig.platform_code,
          platform_name: fullConfig.platform_name,
          app_key: fullConfig.app_key || '',
          master_secret: fullConfig.master_secret || '',
          dev_key: fullConfig.dev_key || '',
          api_dev_secret: fullConfig.api_dev_secret || '',
          sign_id: fullConfig.sign_id || '',
          template_id: fullConfig.template_id || '',
          api_url: fullConfig.api_url || 'https://api.sms.jpush.cn/v1',
          rate_limit_per_ip: fullConfig.rate_limit_per_ip || 10,
          rate_limit_per_phone: fullConfig.rate_limit_per_phone || 5,
          code_expire_minutes: fullConfig.code_expire_minutes || 5,
          code_length: fullConfig.code_length || 6
        })
      }
    } else {
      setEditingConfig(null)
      setFormData({
        platform_code: '',
        platform_name: '',
        app_key: '',
        master_secret: '',
        dev_key: '',
        api_dev_secret: '',
        sign_id: '',
        template_id: '',
        api_url: 'https://api.sms.jpush.cn/v1',
        rate_limit_per_ip: 10,
        rate_limit_per_phone: 5,
        code_expire_minutes: 5,
        code_length: 6
      })
    }
    setShowModal(true)
  }

  // 保存配置
  const handleSave = async () => {
    if (!formData.platform_code || !formData.platform_name) {
      toast.error('请输入平台代码和名称')
      return
    }

    try {
      if (editingConfig) {
        const response = await smsConfigApi.update(editingConfig.id, formData)
        if (response.success) {
          toast.success('配置更新成功')
          setShowModal(false)
          loadConfigs()
        } else {
          toast.error(response.error?.message || '更新失败')
        }
      } else {
        const response = await smsConfigApi.create(formData)
        if (response.success) {
          toast.success('配置创建成功')
          setShowModal(false)
          loadConfigs()
        } else {
          toast.error(response.error?.message || '创建失败')
        }
      }
    } catch (error) {
      toast.error('操作失败')
    }
  }

  // 启用平台
  const handleActivate = async (config: SmsConfig) => {
    try {
      const response = await smsConfigApi.activate(config.id)
      if (response.success) {
        toast.success(response.message || '已切换平台')
        loadConfigs()
        loadBalance()
      } else {
        toast.error(response.error?.message || '切换失败')
      }
    } catch (error) {
      toast.error('操作失败')
    }
  }

  // 删除配置
  const handleDelete = async (config: SmsConfig) => {
    if (!confirm(`确定要删除 ${config.platform_name} 吗？`)) return

    try {
      const response = await smsConfigApi.delete(config.id)
      if (response.success) {
        toast.success('删除成功')
        loadConfigs()
      } else {
        toast.error(response.error?.message || '删除失败')
      }
    } catch (error) {
      toast.error('删除失败')
    }
  }

  // 测试发送
  const handleTest = async () => {
    if (!testPhone || !/^1[3-9]\d{9}$/.test(testPhone)) {
      toast.error('请输入有效的手机号')
      return
    }

    setTesting(true)
    setTestResult(null)
    setVerifyResult(null) // 清空之前的验证结果
    setVerifyCode('') // 清空验证码输入
    try {
      const response = await smsConfigApi.test({ phone: testPhone })
      setTestResult(response)
      if (response.success) {
        toast.success('测试短信发送成功')
        loadBalance() // 刷新余量
      } else {
        toast.error(response.error?.message || '发送失败')
      }
    } catch (error) {
      toast.error('测试失败')
    } finally {
      setTesting(false)
    }
  }

  // 测试验证验证码（与学生注册使用相同逻辑）
  const handleVerify = async () => {
    if (!testPhone || !/^1[3-9]\d{9}$/.test(testPhone)) {
      toast.error('请输入有效的手机号')
      return
    }
    if (!verifyCode || verifyCode.length < 4) {
      toast.error('请输入完整的验证码')
      return
    }

    setVerifying(true)
    setVerifyResult(null)
    try {
      const response = await smsConfigApi.verify({ phone: testPhone, code: verifyCode })
      setVerifyResult(response)
      if (response.success) {
        toast.success('验证码验证通过')
      } else {
        toast.error(response.error?.message || '验证失败')
      }
    } catch (error) {
      toast.error('验证请求失败')
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div className="p-6">
      {/* 标题和操作 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <DevicePhoneMobileIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">短信验证码配置</h1>
            <p className="text-sm text-gray-500">管理短信平台、验证码设置和防刷限制</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowTestModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <BeakerIcon className="w-5 h-5" />
            测试发送
          </button>
          <button
            onClick={() => openEditModal()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="w-5 h-5" />
            添加平台
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* 今日发送 */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">今日发送</p>
              <p className="text-2xl font-bold text-blue-600">{stats?.today?.total_sent || 0}</p>
            </div>
            <ChartBarIcon className="w-10 h-10 text-blue-100" />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            已验证: {stats?.today?.verified || 0} | 过期: {stats?.today?.expired || 0}
          </p>
        </div>

        {/* 本周发送 */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">本周发送</p>
              <p className="text-2xl font-bold text-purple-600">{stats?.week_total || 0}</p>
            </div>
            <ChartBarIcon className="w-10 h-10 text-purple-100" />
          </div>
        </div>

        {/* 短信余量 */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">应用余量</p>
              <p className="text-2xl font-bold text-green-600">
                {balanceLoading ? '...' : (balance?.app_balance ?? '-')}
              </p>
            </div>
            <CurrencyDollarIcon className="w-10 h-10 text-green-100" />
          </div>
          <button
            onClick={loadBalance}
            className="text-xs text-blue-600 hover:text-blue-700 mt-2"
          >
            刷新余量
          </button>
        </div>

        {/* 账号余量 */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">账号余量</p>
              <p className="text-2xl font-bold text-orange-600">
                {balanceLoading ? '...' : (balance?.dev_balance ?? '-')}
              </p>
            </div>
            <CurrencyDollarIcon className="w-10 h-10 text-orange-100" />
          </div>
        </div>
      </div>

      {/* 平台列表 */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : configs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <DevicePhoneMobileIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>暂无短信平台配置</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">平台</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">配置状态</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">防刷设置</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">验证码设置</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {configs.map((config) => (
                <tr key={config.id} className={config.is_active ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{config.platform_name}</p>
                      <p className="text-sm text-gray-500">{config.platform_code}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {config.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        <CheckCircleIcon className="w-4 h-4" />
                        当前使用
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                        <XCircleIcon className="w-4 h-4" />
                        未启用
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1 text-xs">
                      <span className={config.has_app_key ? 'text-green-600' : 'text-red-500'}>
                        AppKey: {config.has_app_key ? '✓' : '✗'}
                      </span>
                      <span className={config.has_master_secret ? 'text-green-600' : 'text-red-500'}>
                        MasterSecret: {config.has_master_secret ? '✓' : '✗'}
                      </span>
                      <span className={config.sign_id ? 'text-green-600' : 'text-red-500'}>
                        签名ID: {config.sign_id || '未设置'}
                      </span>
                      <span className={config.template_id ? 'text-green-600' : 'text-red-500'}>
                        模板ID: {config.template_id || '未设置'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">
                      <p>同IP: {config.rate_limit_per_ip}次/小时</p>
                      <p>同号: {config.rate_limit_per_phone}次/小时</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">
                      <p>有效期: {config.code_expire_minutes}分钟</p>
                      <p>长度: {config.code_length}位</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {!config.is_active && (
                        <button
                          onClick={() => handleActivate(config)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="启用"
                        >
                          <CheckCircleIcon className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => openEditModal(config)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="编辑"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      {!config.is_active && (
                        <button
                          onClick={() => handleDelete(config)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="删除"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b bg-blue-50">
              <h2 className="text-lg font-semibold text-blue-900">
                {editingConfig ? '编辑短信平台' : '添加短信平台'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              {/* 基本信息 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    平台代码 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.platform_code}
                    onChange={(e) => setFormData({ ...formData, platform_code: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="如：jiguang"
                    disabled={!!editingConfig}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    平台名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.platform_name}
                    onChange={(e) => setFormData({ ...formData, platform_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="如：极光短信"
                  />
                </div>
              </div>

              {/* API配置 */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">API 配置</h3>
                  <button
                    type="button"
                    onClick={() => setShowSecrets(!showSecrets)}
                    className="text-sm text-blue-600 flex items-center gap-1"
                  >
                    {showSecrets ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    {showSecrets ? '隐藏' : '显示'}密钥
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API 地址</label>
                    <input
                      type="text"
                      value={formData.api_url}
                      onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={formData.platform_code === 'tencent' ? 'https://sms.tencentcloudapi.com' : 'https://api.sms.jpush.cn/v1'}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {formData.platform_code === 'tencent' ? 'SecretId' : 'AppKey'}
                      </label>
                      <input
                        type={showSecrets ? 'text' : 'password'}
                        value={formData.app_key}
                        onChange={(e) => setFormData({ ...formData, app_key: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {formData.platform_code === 'tencent' ? 'SecretKey' : 'MasterSecret'}
                      </label>
                      <input
                        type={showSecrets ? 'text' : 'password'}
                        value={formData.master_secret}
                        onChange={(e) => setFormData({ ...formData, master_secret: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {formData.platform_code === 'tencent' ? '短信签名内容 (SignName)' : '签名ID'}
                      </label>
                      <input
                        type="text"
                        value={formData.sign_id}
                        onChange={(e) => setFormData({ ...formData, sign_id: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder={formData.platform_code === 'tencent' ? '如：腾讯云' : '极光签名ID'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">模板ID</label>
                      <input
                        type="text"
                        value={formData.template_id}
                        onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {formData.platform_code === 'tencent' ? 'SmsSdkAppId' : 'DevKey (可选)'}
                      </label>
                      <input
                        type={showSecrets ? 'text' : 'password'}
                        value={formData.dev_key}
                        onChange={(e) => setFormData({ ...formData, dev_key: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder={formData.platform_code === 'tencent' ? '1400开头的数字ID' : '用于查询账号余量'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">API DevSecret (可选)</label>
                      <input
                        type={showSecrets ? 'text' : 'password'}
                        value={formData.api_dev_secret}
                        onChange={(e) => setFormData({ ...formData, api_dev_secret: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 防刷设置 */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">防刷设置</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">同一IP每小时最大次数</label>
                    <input
                      type="number"
                      value={formData.rate_limit_per_ip}
                      onChange={(e) => setFormData({ ...formData, rate_limit_per_ip: parseInt(e.target.value) || 10 })}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">同一手机号每小时最大次数</label>
                    <input
                      type="number"
                      value={formData.rate_limit_per_phone}
                      onChange={(e) => setFormData({ ...formData, rate_limit_per_phone: parseInt(e.target.value) || 5 })}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              {/* 验证码设置 */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">验证码设置</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">有效期（分钟）</label>
                    <input
                      type="number"
                      value={formData.code_expire_minutes}
                      onChange={(e) => setFormData({ ...formData, code_expire_minutes: parseInt(e.target.value) || 5 })}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                      max="30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">验证码位数</label>
                    <input
                      type="number"
                      value={formData.code_length}
                      onChange={(e) => setFormData({ ...formData, code_length: parseInt(e.target.value) || 6 })}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="4"
                      max="8"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 测试发送弹窗 */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b bg-green-50">
              <h2 className="text-lg font-semibold text-green-900">测试发送验证码</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">手机号</label>
                <input
                  type="text"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="请输入测试手机号"
                  maxLength={11}
                />
              </div>

              {testResult && (
                <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                  {testResult.success ? (
                    <div className="text-green-700">
                      <p className="font-medium">✓ 发送成功</p>
                      <p className="text-sm mt-1">消息ID: {testResult.data?.msg_id}</p>
                      <p className="text-sm text-gray-600 mt-1">验证码已发送到手机，请查看短信</p>
                    </div>
                  ) : (
                    <div className="text-red-700">
                      <p className="font-medium">✗ 发送失败</p>
                      <p className="text-sm mt-1">错误码: {testResult.error?.code}</p>
                      <p className="text-sm">{testResult.error?.message}</p>
                    </div>
                  )}
                </div>
              )}

              {/* 验证码验证测试 - 发送成功后显示 */}
              {testResult?.success && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">验证验证码（与学生注册使用相同逻辑）</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                      className="flex-1 px-3 py-2 border rounded-lg"
                      placeholder="请输入收到的验证码"
                      maxLength={8}
                    />
                    <button
                      onClick={handleVerify}
                      disabled={verifying || !verifyCode}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                    >
                      {verifying ? '验证中...' : '验证'}
                    </button>
                  </div>
                  
                  {verifyResult && (
                    <div className={`mt-3 p-3 rounded-lg ${verifyResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                      {verifyResult.success ? (
                        <div className="text-green-700">
                          <p className="font-medium">✓ 验证码正确</p>
                        </div>
                      ) : (
                        <div className="text-red-700">
                          <p className="font-medium">✗ 验证失败</p>
                          <p className="text-sm">{verifyResult.error?.message}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowTestModal(false)
                  setTestPhone('')
                  setTestResult(null)
                  setVerifyCode('')
                  setVerifyResult(null)
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                关闭
              </button>
              <button
                onClick={handleTest}
                disabled={testing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {testing ? '发送中...' : '发送测试'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

